import { Fragment, type MouseEvent, type ReactNode } from "react";
import { Link } from "@corpuscomplete/web/components/link";
import { TemporalHelpers } from "@corpuscomplete/core/common/date-time";
import { DateTime } from "@corpuscomplete/core-ui/common/datetime";
import { FoodLogOnDate } from "@corpuscomplete/core-ui/nutrition/food-log";
import { Food } from "@corpuscomplete/core/nutrition/food";
import type { FoodLog } from "@corpuscomplete/core/nutrition/food-log";
import { FoodLogOnDateDialog } from "../components/dialog";
import { FoodLogOnDateEntryItem } from "../components/food-item-input";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { NutrientsProgress } from "@corpuscomplete/web/components/nutrition/nutrient-progress";
import { NutrientAmountsLabel } from "@corpuscomplete/web/components/nutrition/nutrients-label";
import { CalendarDateList } from "@corpuscomplete/web/components/common/calendar-date-list";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { ChevronLeft, ChevronRight, Plus, RotateCcw, X } from "lucide-react";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { FoodItemGroup } from "@corpuscomplete/web/components/nutrition/food-item-group";

function stopActionEvent(event: MouseEvent) {
  event.preventDefault();
  event.stopPropagation();
}

function Label({ label }: { label: string }) {
  return (
    <Text variant="xs" className="mb-2 font-medium text-amber-900">
      {label}
    </Text>
  );
}

function SourceGroupCandidateItem({
  sourceGroupCandidate,
}: {
  sourceGroupCandidate: FoodLog.SourceGroupCandidate;
}) {
  const { createSourceGroup } = FoodLogOnDate.useMutations();

  return (
    <FoodItemGroup
      label={sourceGroupCandidate.label}
      items={sourceGroupCandidate.items}
      defaultExpanded={false}
      renderActions={() => (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={(event) => {
            stopActionEvent(event);
            createSourceGroup(sourceGroupCandidate);
          }}
        >
          <Plus className="size-3.5" />
          Add to log
        </Button>
      )}
    />
  );
}

function SourceGroupItem({ sourceGroup }: { sourceGroup: FoodLog.SourceGroup }) {
  const { deleteSourceGroup, resetSourceGroup } = FoodLogOnDate.useMutations();
  const canReset =
    !!sourceGroup.source && !Food.itemsEqual(sourceGroup.entries, sourceGroup.source.items);

  return (
    <FoodItemGroup
      label={sourceGroup.source?.label ?? "Planned meal"}
      items={sourceGroup.entries}
      defaultExpanded={false}
      renderActions={() => (
        <>
          {canReset && (
            <Button
              variant="ghost"
              size="icon-sm"
              type="button"
              aria-label={`Reset ${sourceGroup.source?.label ?? "source-backed food log group"}`}
              className="text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={(event) => {
                stopActionEvent(event);
                resetSourceGroup(sourceGroup);
              }}
            >
              <RotateCcw className="size-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            type="button"
            aria-label={`Delete ${sourceGroup.source?.label ?? "source-backed food log group"}`}
            className="text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={(event) => {
              stopActionEvent(event);
              deleteSourceGroup(sourceGroup.id);
            }}
          >
            <X className="size-4" />
          </Button>
        </>
      )}
      renderContent={() => (
        <>
          {sourceGroup.entries.map((entry) => (
            <FoodLogOnDateEntryItem key={entry.id} entry={entry} />
          ))}
        </>
      )}
    />
  );
}

/**
 * Date-scoped food log page composition boundary.
 * Contract: passes route date and user identity into
 * @corpuscomplete/core-ui FoodLogOnDate, while supplying web-specific presentational slots.
 */
export function FoodLogDatePage({ date }: { date: Temporal.PlainDate }) {
  const { userId } = useAuth();
  return (
    <FoodLogOnDate.Provider userId={userId} date={date}>
      <div className="flex h-full min-h-0 flex-col items-stretch overflow-y-auto">
        <Header className="sticky top-0 z-10" />
        <Timeline />
      </div>
    </FoodLogOnDate.Provider>
  );
}

function Header({ className }: { className?: string }) {
  const { date } = FoodLogOnDate.useContext();
  const foodItems = FoodLogOnDate.useFoodItems();
  const programDay = FoodLogOnDate.useProgramDay();
  const { isLoading } = FoodLogOnDate.useLoadingState();

  return (
    <div className={cn("mb-2 border-b border-b-gray-200 pb-2", className)}>
      <div className="flex flex-row items-center justify-center gap-2 pb-2">
        <DateLink date={date.subtract({ days: 1 })}>
          <ChevronLeft />
        </DateLink>
        <Text variant="sm">{TemporalHelpers.formatDate(date, "monthDayYear")}</Text>
        <DateLink date={date.add({ days: 1 })}>
          <ChevronRight />
        </DateLink>
      </div>
      {!isLoading && programDay ? (
        <NutrientsProgress targets={programDay.nutrientTargets} actual={{ items: foodItems }} />
      ) : (
        <NutrientAmountsLabel items={foodItems} />
      )}
    </div>
  );
}

function Timeline() {
  const session = FoodLogOnDate.useAddEntrySession();
  const items = FoodLogOnDate.useItems();
  const timeZone = DateTime.useTimeZone();
  const { isLoading } = FoodLogOnDate.useLoadingState();

  return (
    <>
      {!isLoading && (
        <CalendarDateList
          items={items}
          timeZone={timeZone}
          renderItem={(item) => <TimelineItem item={item} />}
          onPressTimeAdd={session.openAtTime}
        />
      )}
      {session.isOpen && (
        <FoodLogOnDate.AddEntryDialogProvider
          initialTime={session.initialTime}
          onCreateEntries={session.createEntries}
        >
          <FoodLogOnDateDialog open={session.isOpen} onClose={session.close} />
        </FoodLogOnDate.AddEntryDialogProvider>
      )}
    </>
  );
}

function TimelineItem({ item }: { item: FoodLog.Item }) {
  if (item.kind === "entry") {
    return <FoodLogOnDateEntryItem entry={item} />;
  }
  if (item.kind === "sourceGroup") {
    return <SourceGroupItem sourceGroup={item} />;
  }
  if (item.kind === "sourceGroupCandidate") {
    return <SourceGroupCandidateItem sourceGroupCandidate={item} />;
  }

  return (
    <>
      <Label label={item.label} />
      {item.candidates.map((candidate) => (
        <Fragment key={candidate.id}>
          <SourceGroupCandidateItem sourceGroupCandidate={candidate} />
        </Fragment>
      ))}
    </>
  );
}

function DateLink({ children, date }: { children: ReactNode; date: Temporal.PlainDate }) {
  return (
    <Link to="/personal/food-log/$date" params={{ date }}>
      {children}
    </Link>
  );
}
